import { SignIn } from '@clerk/clerk-react';
import { Link } from '@tanstack/react-router';
import { LegalLink, MonoLabel, Row, StripePlaceholder } from '@/component';
import { useLegalPages, usePublicBillboard } from '@/hook';
import { SignedOutLayout } from './SignedOutLayout';

const SKELETON_RANKS = [1, 2, 3, 4, 5];

const rankClass = (rank: number) =>
  `w-[22px] shrink-0 text-[15px] font-semibold tabular-nums ${rank === 1 ? 'text-acc' : 'text-faint'}`;

/** Signed-out screen at /sign-in: hero and the public top five on the left,
 * Clerk sign-in on the surface column. A visitor sees what the room is actually
 * singing, so the chart is a real unauthenticated read and the section hides
 * when it is empty, which is every week while the beta is closed. The legal
 * line sits right under the card's Continue and links both documents once the
 * deployment names them. Sign-up is by invitation, so the way in for everyone
 * else, the waitlist, comes last. Signing in or up lands on `destination`. */
export function SignedOutPage({ destination }: { destination: string }) {
  const chart = usePublicBillboard();
  const legal = useLegalPages();
  const topFive = chart?.length === 0 ? undefined : (
    <div>
      <div className="flex items-baseline justify-between gap-4 border-b border-line pb-[11px]">
        <h2 className="text-[15px] font-semibold tracking-[-0.01em]">Most queued this week</h2>
        <MonoLabel>Everyone</MonoLabel>
      </div>
      {chart === undefined
        ? SKELETON_RANKS.map((rank) => (
            <Row key={rank} className="max-w-[560px] py-3">
              <span className={rankClass(rank)}>{rank}</span>
              <StripePlaceholder className="h-3 flex-1 rounded-sm p-0" />
            </Row>
          ))
        : chart.map((row) => (
            <Row key={row.trackId} className="max-w-[560px] py-3">
              <span className={rankClass(row.rank)}>{row.rank}</span>
              <div className="min-w-0 flex-1 truncate text-sm font-semibold">{row.title}</div>
              <span className="shrink-0 text-[13px] text-mute">{row.artist}</span>
            </Row>
          ))}
    </div>
  );
  return (
    <SignedOutLayout aside={topFive}>
      <SignIn
        routing="path"
        path="/sign-in"
        withSignUp
        forceRedirectUrl={destination}
        signUpForceRedirectUrl={destination}
      />
      <p className="mt-4 text-center text-[13px] leading-[1.6] text-mute">
        By continuing you agree to the <LegalLink href={legal?.terms}>terms</LegalLink> and the{' '}
        <LegalLink href={legal?.privacy}>privacy policy</LegalLink>.
      </p>
      <p className="mt-6 text-center text-[13px] text-mute">
        The beta is invite-only.{' '}
        <Link to="/waitlist" className="font-semibold text-ink hover:text-acc">
          Join the waitlist
        </Link>
      </p>
    </SignedOutLayout>
  );
}
